import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { countriesApi, Country } from '@/api/countries.api';

interface ContinentGroup {
  id: string;
  name: string;
  countries: Country[];
  color: string;
  hoverColor: string;
}

const CONTINENT_STYLES: Record<string, { color: string; hoverColor: string }> = {
  'Asia': { color: '#059669', hoverColor: '#10B981' },
  'Europe': { color: '#2563EB', hoverColor: '#3B82F6' },
  'North America': { color: '#7C3AED', hoverColor: '#8B5CF6' },
  'South America': { color: '#7C3AED', hoverColor: '#8B5CF6' },
  'Middle East': { color: '#D97706', hoverColor: '#F59E0B' },
  'Africa': { color: '#DC2626', hoverColor: '#EF4444' },
  'Oceania': { color: '#059669', hoverColor: '#10B981' },
  'Central Asia': { color: '#D97706', hoverColor: '#F59E0B' },
};

const DEFAULT_STYLE = { color: '#6B7280', hoverColor: '#9CA3AF' };

const CONTINENT_ORDER = [
  'Asia', 'Europe', 'North America', 'South America',
  'Middle East', 'Africa', 'Oceania', 'Central Asia',
];

// Lightweight count display - shows final value directly without animation
const useCountUp = (end: number, _duration: number = 800, _start: number = 0) => {
  const [count, setCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible) return;
    setCount(end);
  }, [isVisible, end]);

  return { count, ref };
};

// Flag component
const FlagIcon: React.FC<{ code: string; name: string; isActive: boolean }> = ({ code, name, isActive }) => {
  return (
    <div className="relative group">
      <img
        src={`https://flagcdn.com/w40/${code.toLowerCase()}.png`}
        srcSet={`https://flagcdn.com/w80/${code.toLowerCase()}.png 2x`}
        alt={name}
        className={`w-8 h-6 object-cover rounded shadow-sm transition-all duration-500 ease-out ${
          isActive ? 'grayscale-0 scale-110' : 'grayscale opacity-70'
        }`}
        loading="lazy"
      />
      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap pointer-events-none z-10">
        {name}
      </div>
    </div>
  );
};

interface GlobalNetworkProps {
  embedded?: boolean;
}

const GlobalNetwork: React.FC<GlobalNetworkProps> = ({ embedded = false }) => {
  const navigate = useNavigate();
  const [activeContinent, setActiveContinent] = useState<string>('Asia');
  const { count: partnersCount, ref: partnersRef } = useCountUp(1500);
  const { count: formulationsCount, ref: formulationsRef } = useCountUp(200000);

  const { data: countries = [] } = useQuery({
    queryKey: ['countries'],
    queryFn: async () => {
      const response = await countriesApi.getAll();
      return response.data?.data || [];
    },
    staleTime: 60 * 60 * 1000,
  });

  // Group countries by continent from DB data
  const continentData: ContinentGroup[] = useMemo(() => {
    const groupMap = new Map<string, Country[]>();
    countries.forEach((country) => {
      const continent = country.continent || 'Other';
      if (!groupMap.has(continent)) groupMap.set(continent, []);
      groupMap.get(continent)!.push(country);
    });

    // Sort by preferred order, then append any extras
    const ordered: ContinentGroup[] = [];
    const seen = new Set<string>();

    for (const name of CONTINENT_ORDER) {
      const list = groupMap.get(name);
      if (list && list.length > 0) {
        const style = CONTINENT_STYLES[name] || DEFAULT_STYLE;
        ordered.push({
          id: name.toLowerCase().replace(/\s+/g, '-'),
          name,
          countries: list.sort((a, b) => a.name_en.localeCompare(b.name_en)),
          ...style,
        });
        seen.add(name);
      }
    }

    groupMap.forEach((list, name) => {
      if (!seen.has(name) && list.length > 0) {
        const style = CONTINENT_STYLES[name] || DEFAULT_STYLE;
        ordered.push({
          id: name.toLowerCase().replace(/\s+/g, '-'),
          name,
          countries: list.sort((a, b) => a.name_en.localeCompare(b.name_en)),
          ...style,
        });
      }
    });

    return ordered;
  }, [countries]);

  const totalCountries = countries.length;

  useEffect(() => {
    if (!embedded) {
      document.title = 'Global Network | NextPangaea';
    }
  }, [embedded]);

  // Set default active continent when data loads
  useEffect(() => {
    if (continentData.length > 0 && !continentData.find(c => c.name === activeContinent)) {
      setActiveContinent(continentData[0].name);
    }
  }, [continentData, activeContinent]);

  const activeData = continentData.find((c) => c.name === activeContinent) || continentData[0];

  if (!activeData) {
    return null;
  }

  const content = (
    <section className="relative py-16 md:py-24 bg-white overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-gradient-to-bl from-np-50 to-transparent rounded-full -translate-y-1/2 translate-x-1/4 opacity-60" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-gradient-to-tr from-gray-100 to-transparent rounded-full translate-y-1/2 -translate-x-1/4 opacity-60" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12 md:mb-16">
          <p className="text-np-600 font-medium tracking-wide uppercase text-sm mb-2">
            Our Global Presence
          </p>
          <h2 className="font-playfair text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            Global Network
          </h2>
          <p className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto">
            Connecting Korean Beauty Excellence to Partners Worldwide
          </p>

          {/* Stats */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-6 sm:gap-12 mt-10">
            <div ref={partnersRef} className={`text-center transition-opacity duration-500 ${partnersCount > 0 ? 'opacity-100' : 'opacity-0'}`}>
              <div className="text-4xl md:text-5xl font-bold text-gray-900">
                {partnersCount.toLocaleString()}<span className="text-np-500">+</span>
              </div>
              <div className="text-sm md:text-base text-gray-500 mt-1">Partners</div>
            </div>
            <div className="hidden sm:block w-px h-12 bg-gray-200" />
            <div ref={formulationsRef} className={`text-center transition-opacity duration-500 ${formulationsCount > 0 ? 'opacity-100' : 'opacity-0'}`}>
              <div className="text-4xl md:text-5xl font-bold text-gray-900">
                {formulationsCount.toLocaleString()}<span className="text-np-500">+</span>
              </div>
              <div className="text-sm md:text-base text-gray-500 mt-1">Formulations</div>
            </div>
          </div>
        </div>

        {/* Region Header */}
        <div className="text-center mb-6">
          <h3 className="text-lg md:text-xl font-semibold text-gray-700">Region</h3>
        </div>

        {/* Continent Category Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4 mb-6">
          {continentData.map((continent) => {
            const isActive = activeContinent === continent.name;
            return (
              <div
                key={continent.id}
                className={`group relative p-4 md:p-5 rounded-xl border-2 transition-all duration-400 ease-out cursor-pointer ${
                  isActive
                    ? 'border-transparent shadow-lg scale-[1.02]'
                    : 'border-gray-100 bg-white hover:border-gray-200 hover:shadow-md'
                }`}
                style={{
                  backgroundColor: isActive ? continent.hoverColor : undefined,
                }}
                onClick={() => setActiveContinent(continent.name)}
              >
                {/* Color indicator */}
                <div
                  className={`absolute top-3 right-3 w-3 h-3 rounded-full transition-all duration-300 ${
                    isActive ? 'scale-125' : 'scale-100'
                  }`}
                  style={{ backgroundColor: isActive ? '#fff' : continent.color }}
                />

                {/* Content */}
                <div className="text-left">
                  <h3
                    className={`font-semibold text-sm md:text-base mb-1 transition-colors duration-300 ${
                      isActive ? 'text-white' : 'text-gray-900'
                    }`}
                  >
                    {continent.name}
                  </h3>
                  <p
                    className={`text-xs md:text-sm transition-colors duration-300 ${
                      isActive ? 'text-white/80' : 'text-gray-500'
                    }`}
                  >
                    {continent.countries.length} Countries
                  </p>
                </div>

                {/* Hover arrow */}
                <div
                  className={`absolute bottom-3 right-3 transition-all duration-300 ${
                    isActive ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-2'
                  }`}
                >
                  <svg
                    className="w-4 h-4 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </div>
              </div>
            );
          })}
        </div>

        {/* Country List Slider Panel */}
        <div
          className="rounded-2xl border border-gray-100 shadow-sm overflow-hidden transition-all duration-500 ease-out"
          style={{
            backgroundColor: `${activeData.hoverColor}10`,
          }}
        >
          <div className="p-6 animate-fadeIn" key={activeContinent}>
            {/* Header */}
            <div className="flex items-center gap-3 mb-4">
              <div
                className="w-4 h-4 rounded-full"
                style={{ backgroundColor: activeData.hoverColor }}
              />
              <h3 className="text-lg font-semibold text-gray-900">
                {activeData.name}
              </h3>
              <span className="text-sm text-gray-500">
                ({activeData.countries.length} Countries & Regions)
              </span>
            </div>

            {/* Country Grid - Show all countries */}
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
              {activeData.countries.map((country, index) => (
                <div
                  key={country.code}
                  className="flex items-center gap-3 px-4 py-3 bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md hover:border-gray-200 transition-all duration-300"
                  style={{
                    animation: 'slideInFromRight 0.3s ease-out forwards',
                    animationDelay: `${index * 20}ms`,
                    opacity: 0,
                  }}
                >
                  <FlagIcon code={country.code} name={country.name_en} isActive={true} />
                  <span className="text-sm font-medium text-gray-700 truncate">
                    {country.name_en}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom Text */}
        <div className="mt-12 text-center">
          <p className="text-gray-500 text-sm md:text-base">
            Partnering with <span className="text-gray-900 font-semibold">{totalCountries} countries</span> to deliver the excellence of K-Beauty worldwide
          </p>
        </div>

        {/* Request Sample CTA Button */}
        <div className="mt-10 text-center">
          <button
            onClick={() => navigate('/request')}
            className="inline-flex items-center gap-2 px-8 py-4 bg-np-600 hover:bg-np-700 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Request a Sample
          </button>
          <p className="mt-3 text-sm text-gray-500">
            Start your K-Beauty journey with us today
          </p>
        </div>
      </div>

      {/* Custom Styles */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideInFromRight {
          from {
            opacity: 0;
            transform: translateX(20px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.4s ease-out;
        }
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </section>
  );

  if (embedded) {
    return content;
  }

  return <div className="min-h-screen bg-white">{content}</div>;
};

export default GlobalNetwork;
